import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { 
  ArrowLeft, CheckCircle, Clock, Shield, FileText, Users, 
  MapPin, Calendar, Music, Flame, Car, Zap, Megaphone,
  Building2, Sparkles, AlertCircle, Phone, Menu, X
} from 'lucide-react';
import Footer from '@/components/Footer';
import { useAuth } from '@/context/AuthContext';
import ScrollingLogo from '@/components/ScrollingLogo';

export default function EventPermissionsPage() {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [eventDate, setEventDate] = useState(null);
  const [formData, setFormData] = useState({
    event_name: '',
    event_type: '',
    event_date: '',
    location: '',
    city: '',
    state: '',
    attendees: '',
    permission_types: [],
    special_requirements: '',
    contact_name: '',
    contact_email: '',
    contact_phone: ''
  });

  // Scroll effect for transparent header
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const permissionCategories = {
    'Safety & Security': [
      { id: 'police', name: 'Police Permission', icon: Shield, desc: 'Required for large gatherings (500+ people)', processing: '7-10 days' },
      { id: 'fire', name: 'Fire Safety NOC', icon: Flame, desc: 'Fire department approval for venue safety', processing: '5-7 days' },
      { id: 'medical', name: 'Medical Emergency Setup', icon: AlertCircle, desc: 'Ambulance and medical staff on standby', processing: '3-5 days' },
      { id: 'security', name: 'Security Clearance', icon: Shield, desc: 'Private security approval', processing: '5-7 days' }
    ],
    'Entertainment & Sound': [
      { id: 'sound', name: 'Sound Permission', icon: Megaphone, desc: 'For amplified sound/music (beyond 10 PM)', processing: '3-5 days' },
      { id: 'music', name: 'Music License', icon: Music, desc: 'PPL/IPRS license for copyrighted music', processing: '7-14 days' },
      { id: 'fireworks', name: 'Fireworks Permission', icon: Sparkles, desc: 'For pyrotechnics/fireworks display', processing: '10-15 days' }
    ],
    'Infrastructure & Facilities': [
      { id: 'stage', name: 'Stage Construction Permission', icon: Building2, desc: 'For temporary stage/structure setup', processing: '5-7 days' },
      { id: 'tent', name: 'Tent/Pandal Permission', icon: Building2, desc: 'For temporary outdoor structures', processing: '5-7 days' },
      { id: 'generator', name: 'Generator Permission', icon: Zap, desc: 'For power generators (noise/fuel)', processing: '3-5 days' },
      { id: 'electrical', name: 'Electrical Safety Certificate', icon: Zap, desc: 'Electrical installation safety approval', processing: '5-7 days' }
    ],
    'Food & Beverages': [
      { id: 'food', name: 'Food License (FSSAI)', icon: FileText, desc: 'For serving food at events', processing: '7-10 days' },
      { id: 'alcohol', name: 'Alcohol License', icon: FileText, desc: 'Liquor serving permit', processing: '15-21 days' }
    ],
    'Traffic & Parking': [
      { id: 'parking', name: 'Parking Permission', icon: Car, desc: 'For designated parking areas', processing: '3-5 days' },
      { id: 'road', name: 'Road Closure Permission', icon: MapPin, desc: 'For processions/road events', processing: '10-15 days' }
    ],
    'Government NOCs': [
      { id: 'municipal', name: 'Municipal Corporation NOC', icon: Building2, desc: 'Local body approval', processing: '7-10 days' },
      { id: 'environmental', name: 'Environmental Clearance', icon: FileText, desc: 'For large outdoor events', processing: '15-30 days' }
    ]
  };

  const handlePermissionToggle = (permissionId) => {
    setFormData(prev => ({
      ...prev,
      permission_types: prev.permission_types.includes(permissionId)
        ? prev.permission_types.filter(p => p !== permissionId)
        : [...prev.permission_types, permissionId]
    }));
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/event-permissions', formData);
      toast.success('Event permission request submitted! We will contact you within 24 hours.');
      setFormData({
        event_name: '',
        event_type: '',
        event_date: '',
        location: '',
        city: '',
        state: '',
        attendees: '',
        permission_types: [],
        special_requirements: '',
        contact_name: '',
        contact_email: '',
        contact_phone: ''
      });
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/80 backdrop-blur-lg border-b border-gray-200 shadow-md' 
            : 'bg-sky-100 backdrop-blur-md border-b border-sky-200 shadow-sm'
        }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_07322645-a840-4848-8247-f88636798b30/artifacts/kyqhtx8f_MCH%20Logo-2.png" 
                alt="My Corporate Hub" 
                className="h-16 w-auto"
              />
              <span className="font-heading text-2xl text-navy font-bold tracking-tight hidden md:block">My Corporate Hub<sup className="text-xs ml-0.5">®</sup></span>
            </Link>
            
            {/* Centered Menus */}
            <div className="hidden md:flex items-center gap-8 absolute left-1/2 transform -translate-x-1/2">
              <Link to="/services" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Services</Link>
              <Link to="/properties" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Properties</Link>
              <Link to="/gifts" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Gifting</Link>
              <Link to="/event-permissions" className="text-gold font-medium text-sm uppercase tracking-wide">Event Permits</Link>
              <Link to="/trainers" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Trainers</Link>
              <Link to="/pricing" className="text-navy hover:text-gold font-medium text-sm uppercase tracking-wide transition-colors">Pricing</Link>
            </div>

            <div className="flex items-center gap-4">
              {/* Mobile Menu Button */}
              <button 
                className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6 text-navy" /> : <Menu className="w-6 h-6 text-navy" />}
              </button>
              
              {user ? (
                <>
                  <Link to={`/dashboard/${user.role}`} className="hidden md:block">
                    <Button variant="ghost" className="text-navy font-medium">Dashboard</Button>
                  </Link>
                  <Button onClick={logout} variant="outline" className="border-navy text-navy hidden md:flex">Logout</Button>
                </>
              ) : (
                <>
                  <Link to="/?login=true" className="hidden md:block">
                    <Button variant="ghost" className="text-navy font-medium">Sign In</Button>
                  </Link>
                  <Link to="/bulk-rfp/new">
                    <Button className="bg-gold hover:bg-gold-light text-navy-900 font-bold shadow-lg px-4 sm:px-6 whitespace-nowrap text-sm sm:text-base">Get Started</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
          
          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden bg-white border-t border-gray-200 py-4 px-4 space-y-2">
              <Link to="/services" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Services</Link>
              <Link to="/properties" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Properties</Link>
              <Link to="/gifts" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Gifting</Link>
              <Link to="/event-permissions" className="block py-3 px-4 text-gold bg-sky-50 rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Event Permits</Link>
              <Link to="/trainers" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Trainers</Link>
              <Link to="/pricing" className="block py-3 px-4 text-navy hover:bg-sky-50 rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Pricing</Link>
              <div className="pt-3 border-t border-gray-200 mt-3">
                {user ? (
                  <>
                    <Link to={`/dashboard/${user.role}`} className="block py-3 px-4 bg-navy text-white rounded-lg font-medium text-center mb-2" onClick={() => setMobileMenuOpen(false)}>
                      Dashboard
                    </Link>
                    <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="block w-full py-3 px-4 border border-navy text-navy rounded-lg font-medium text-center">
                      Logout
                    </button>
                  </>
                ) : (
                  <Link to="/?login=true" className="block py-3 px-4 bg-navy text-white rounded-lg font-medium text-center" onClick={() => setMobileMenuOpen(false)}>
                    Sign In
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <div className="relative text-white py-20 mt-20 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&q=80')" }}
        />
        <div className="absolute inset-0 bg-navy/80" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <span className="inline-block bg-gold/10 text-gold border border-gold/30 px-6 py-2 rounded-full text-sm font-bold uppercase tracking-widest mb-6">
              EVENT PERMITS
            </span>
            <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">Event Permits & Permissions</h1>
            <p className="text-white/90 text-lg max-w-3xl mx-auto">
              Comprehensive event permission assistance for corporate gatherings, conferences, and large-scale events across India. 
              We handle all government approvals and statutory compliances.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Info Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <Card>
            <CardContent className="pt-6">
              <Clock className="w-12 h-12 text-gold mb-4" />
              <h3 className="text-lg font-bold text-navy mb-2">Fast Processing</h3>
              <p className="text-slate-800">Get permissions processed in 3-30 days depending on type</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <CheckCircle className="w-12 h-12 text-gold mb-4" />
              <h3 className="text-lg font-bold text-navy mb-2">100% Compliance</h3>
              <p className="text-slate-800">All permissions are government-verified and legally compliant</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <Phone className="w-12 h-12 text-gold mb-4" />
              <h3 className="text-lg font-bold text-navy mb-2">Dedicated Support</h3>
              <p className="text-slate-800">Personal assistance throughout the permission process</p>
            </CardContent>
          </Card>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Event Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-gold" />
                Event Details
              </CardTitle>
              <CardDescription>Basic information about your event</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Event Name *</Label>
                  <Input
                    value={formData.event_name}
                    onChange={(e) => handleChange('event_name', e.target.value)}
                    placeholder="Annual Sales Conference 2025"
                    required
                  />
                </div>
                <div>
                  <Label>Event Type *</Label>
                  <Select value={formData.event_type} onValueChange={(value) => handleChange('event_type', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select event type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="conference">Conference</SelectItem>
                      <SelectItem value="seminar">Seminar</SelectItem>
                      <SelectItem value="exhibition">Exhibition</SelectItem>
                      <SelectItem value="concert">Concert/Music Event</SelectItem>
                      <SelectItem value="festival">Festival</SelectItem>
                      <SelectItem value="sports">Sports Event</SelectItem>
                      <SelectItem value="wedding">Wedding</SelectItem>
                      <SelectItem value="corporate">Corporate Gathering</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label>Event Date *</Label>
                  <DatePicker
                    selected={eventDate}
                    onChange={(date) => {
                      setEventDate(date);
                      handleChange('event_date', date ? date.toISOString().split('T')[0] : '');
                    }}
                    minDate={new Date()}
                    dateFormat="dd/MM/yyyy"
                    placeholderText="DD/MM/YYYY"
                    className="w-full h-10 px-3 text-base border border-gray-200 rounded-md hover:border-gold focus:border-gold focus:outline-none focus:ring-2 focus:ring-gold/20 cursor-pointer bg-white"
                    calendarClassName="custom-calendar"
                    wrapperClassName="w-full"
                    required
                  />
                </div>
                <div>
                  <Label>Expected Attendees *</Label>
                  <Input
                    type="number"
                    value={formData.attendees}
                    onChange={(e) => handleChange('attendees', e.target.value)}
                    placeholder="500"
                    required
                  />
                </div>
                <div>
                  <Label>City *</Label>
                  <Input
                    value={formData.city}
                    onChange={(e) => handleChange('city', e.target.value)}
                    placeholder="Mumbai"
                    required
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Venue/Location *</Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => handleChange('location', e.target.value)}
                    placeholder="BKC Convention Center"
                    required
                  />
                </div>
                <div>
                  <Label>State *</Label>
                  <Select value={formData.state} onValueChange={(value) => handleChange('state', value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="maharashtra">Maharashtra</SelectItem>
                      <SelectItem value="delhi">Delhi</SelectItem>
                      <SelectItem value="karnataka">Karnataka</SelectItem>
                      <SelectItem value="tamilnadu">Tamil Nadu</SelectItem>
                      <SelectItem value="gujarat">Gujarat</SelectItem>
                      <SelectItem value="rajasthan">Rajasthan</SelectItem>
                      <SelectItem value="goa">Goa</SelectItem>
                      <SelectItem value="up">Uttar Pradesh</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Permission Types */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-gold" />
                Select Required Permissions
              </CardTitle>
              <CardDescription>Choose all permissions you need for your event</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {Object.entries(permissionCategories).map(([category, permissions]) => (
                <div key={category}>
                  <h3 className="text-lg font-semibold text-navy mb-3">{category}</h3>
                  <div className="grid md:grid-cols-2 gap-3">
                    {permissions.map((permission) => (
                      <label
                        key={permission.id}
                        className="flex items-start gap-3 p-4 border rounded-lg hover:border-gold hover:bg-gold/5 cursor-pointer transition-all"
                      >
                        <Checkbox
                          checked={formData.permission_types.includes(permission.id)}
                          onCheckedChange={() => handlePermissionToggle(permission.id)}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <permission.icon className="w-4 h-4 text-gold" />
                            <span className="font-semibold text-slate-900">{permission.name}</span>
                          </div>
                          <p className="text-sm text-slate-800 mb-1">{permission.desc}</p>
                          <p className="text-xs text-gold font-medium">⏱ {permission.processing}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Contact Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-gold" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label>Contact Name *</Label>
                  <Input
                    value={formData.contact_name}
                    onChange={(e) => handleChange('contact_name', e.target.value)}
                    placeholder="John Doe"
                    required
                  />
                </div>
                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => handleChange('contact_email', e.target.value)}
                    placeholder="john@company.com"
                    required
                  />
                </div>
                <div>
                  <Label>Phone *</Label>
                  <Input
                    type="tel"
                    value={formData.contact_phone}
                    onChange={(e) => handleChange('contact_phone', e.target.value)}
                    placeholder="+91 98765 43210"
                    required
                  />
                </div>
              </div>

              <div>
                <Label>Special Requirements/Notes</Label>
                <Textarea
                  value={formData.special_requirements}
                  onChange={(e) => handleChange('special_requirements', e.target.value)}
                  placeholder="Any specific requirements, timeline constraints, or additional information..."
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex justify-center">
            <Button
              type="submit"
              disabled={loading || formData.permission_types.length === 0}
              className="bg-gold hover:bg-gold-light text-navy font-bold px-12 py-6 text-lg shadow-lg"
            >
              {loading ? 'Submitting...' : 'Submit Permission Request'}
            </Button>
          </div>
        </form>

        {/* Help Section */}
        <Card className="mt-12 bg-navy text-white">
          <CardContent className="py-8">
            <h3 className="text-2xl font-bold mb-4">Need Help?</h3>
            <p className="text-slate-300 mb-6">
              Our event permissions team is here to assist you with the entire process. 
              We have partnerships with local authorities across India for faster approvals.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button className="bg-gold hover:bg-gold-light text-navy">
                <Phone className="w-4 h-4 mr-2" />
                Call: +91 98765 43210
              </Button>
              <Button variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">
                Email: permissions@mycorporatehub.com
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Footer */}
      <Footer />

      {/* Fixed Rotating Logo - Bottom Left */}
      <ScrollingLogo />
    </div>
  );
}
